import Button from '@ingka/button';
import Loading, { LoadingBall } from '@ingka/loading';
import arrowUpToBase from '@ingka/ssr-icon/paths/arrow-up-to-base';
import Text from '@ingka/text';
import {
  useGetLotteryDrawQuery,
  useGetLotteryQuery,
  useGetWinnersQuery,
} from '@lotta/self-service/lottery-fe/data-access-state';
import type { LocaleParams } from '@lotta/self-service/lottery-fe/types-router';
import type { Winner } from '@lotta/shared/types-lottery-schemas';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useNavigateWithLocale } from '@lotta/self-service/lottery-fe/feature-hooks';
import styles from './DrawDetails.module.scss';
import { DrawDetailsTable } from './components/draw-details-table/DrawDetailsTable';
import {
  WinnersTable,
  getLatestEvent,
  eventTranslationKeys,
} from './components/winners-table/WinnersTable';

/* eslint-disable-next-line */
export type DrawDetailsProps = {};

export type DrawDetailsParams = {
  lotteryId: string;
  lotteryDrawId: string;
};

export const DrawDetails: React.FC<DrawDetailsProps> = () => {
  // Asserting params to be defined as route will not match if params are omitted.
  const { lotteryId, lotteryDrawId } = useParams<DrawDetailsParams>() as DrawDetailsParams;
  const { countryCode: retailUnit } = useParams<LocaleParams>() as LocaleParams;
  const navigate = useNavigateWithLocale();
  const { t } = useTranslation();

  const {
    data: lotteryResponse,
    isLoading: isLoadingLottery,
    error: lotteryError,
  } = useGetLotteryQuery({ retailUnit, lotteryId });

  const {
    data: lotteryDrawResponse,
    isLoading: isLoadingLotteryDraw,
    error: lotteryDrawError,
  } = useGetLotteryDrawQuery({ retailUnit, lotteryId, lotteryDrawId });

  const {
    data: winnersResponse,
    isLoading: isLoadingWinners,
    error: winnersError,
    refetch: refetchWinners,
  } = useGetWinnersQuery({ retailUnit, lotteryId, lotteryDrawId });

  if (isLoadingLottery || isLoadingLotteryDraw || isLoadingWinners) {
    return (
      <Loading text={`${t('draw-details-fetch-draw-and-winners')}`}>
        <LoadingBall />
      </Loading>
    );
  }

  if (
    lotteryDrawError ||
    !lotteryDrawResponse ||
    winnersError ||
    !winnersResponse ||
    lotteryError ||
    !lotteryResponse
  ) {
    return <Text headingSize="m">{t('general-something-went-wrong')}</Text>;
  }

  const { name, drawConstraints } = lotteryResponse.lottery;
  const { lotteryDraw } = lotteryDrawResponse;
  const { winners } = winnersResponse;

  const exportToCsv = (winners: Winner[]) => {
    const hasStoreConstraint = drawConstraints?.some(
      (constraint) => 'storeIds' in constraint || 'maxNbrWinnersPerStore' in constraint
    );
    const includeStoreIds = hasStoreConstraint && winners.some((winner) => 'storeId' in winner);

    const headers = [
      'Winner',
      'Party-UID',
      ...(includeStoreIds ? ['Store-ID'] : []),
      t('draw-details-header-event-status'),
    ].join(',');

    const CSVCONTENT =
      `data:text/csv;charset=utf-8,${headers}\n` +
      winners
        .map((winner, index) => {
          const latestEvent = getLatestEvent(winner.events);
          const rowData = [
            `Winner ${index + 1}`,
            winner.partyUid,
            ...(includeStoreIds ? [winner.storeId || ''] : []),
            latestEvent !== undefined
              ? t(eventTranslationKeys[latestEvent])
              : t('winner-event-no-events'),
          ];
          return rowData.join(',');
        })
        .join('\n');

    const encodedUri = encodeURI(CSVCONTENT);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'winners.csv');
    document.body.appendChild(link);
    link.click();
  };

  return (
    <div className={styles['container']} data-testid="draw-details-container">
      <Text className={styles['header']}>{t('draw-details-header')}</Text>
      {/* <div className={styles['subtitle-row']}>
        <Text className={styles['subtitle']}>
          // Leaving the subtitle out for now
        </Text>
      </div> */}
      <DrawDetailsTable drawDetails={lotteryDraw} lotteryName={name} />
      <div>
        <hr />
      </div>
      <WinnersTable
        winners={winners}
        lotteryId={lotteryId}
        lotteryDraw={lotteryDraw}
        refetchWinners={refetchWinners}
      />
      <Button
        type="primary"
        ssrIcon={arrowUpToBase}
        iconPosition="leading"
        data-testid="create-lottery-button"
        onClick={() => exportToCsv(winners)}
        className={styles['csv-button']}
      >
        {t('draw-details-export-winner-btn')}
      </Button>
      <Button
        type="secondary"
        className={styles['back-button']}
        onClick={() => navigate(`/lottery/${lotteryId}`)}
      >
        {t('general-back')}
      </Button>
    </div>
  );
};
